import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { enqueueSnackbar } from "notistack";
import { ReactNode, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Export, ExportResponse, ExportsResponse } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Authorizations } from "~/api/types";
import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/ui/button/Button";
import Card from "~/components/ui/card/Card";
import DataTable from "~/components/ui/dataTable/DataTable";
import Spinner from "~/components/ui/spinner/Spinner";
import { formatDate } from "~/helpers/formatDate";
import { messageFromError } from "~/helpers/messageFromError";

import BaseReportsPage from "../baseReportsPage/BaseReportsPage";
import styles from "./ExportsPage.module.scss";
import { ExportsQueryData, upsertCachedExport } from "./exportsCache";

type ExportsResponseWithAuth = ExportsResponse & {
  authorizations?: Authorizations;
};

export type CreateActionContext<Payload> = {
  create: (payload: Payload, options?: { onSuccess?: () => void }) => void;
  isCreating: boolean;
  canCreate: boolean;
  exports: Export[];
};

interface ExportsPageProps<Payload> {
  exportType: Export["type"];
  createFn: (
    siteId: number,
    payload: Payload,
  ) => Promise<{ data: ExportResponse }>;
  pageTitle: string;
  description: string;
  cardTitle?: string;
  buttonLabel?: string;
  successMessage: string;
  emptyText: string;
  /** Inserted after the "Created At" column. */
  extraColumns?: ColumnDef<Export>[];
  /** Rendered between the description and the table, e.g. notices. */
  renderAboveTable?: (context: CreateActionContext<Payload>) => ReactNode;
  /** Replaces the default create button, e.g. to collect a payload first. */
  renderCreateAction?: (context: CreateActionContext<Payload>) => ReactNode;
}

const STATUS_CONFIG: Record<
  Export["status"],
  { label: string; className: string }
> = {
  created: { label: "Created", className: styles.statusCreated },
  processing: { label: "Processing", className: styles.statusProcessing },
  completed: { label: "Completed", className: styles.statusCompleted },
  failed: { label: "Failed", className: styles.statusFailed },
  canceled: { label: "Canceled", className: styles.statusCanceled },
};

const CREATED_AT_COLUMN: ColumnDef<Export> = {
  accessorKey: "created_at",
  header: "Created At",
  size: 260,
  cell: ({ getValue }) => formatDate(getValue<string>()),
};

const TRAILING_COLUMNS: ColumnDef<Export>[] = [
  {
    accessorKey: "status",
    header: "Status",
    size: 140,
    cell: ({ getValue }) => {
      const status = getValue<Export["status"]>();
      const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.created;

      return <span className={config.className}>{config.label}</span>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    size: 80,
    meta: { align: "center" },
    enableSorting: false,
    cell: ({ row }) => {
      const { status, url } = row.original;

      if (status !== "completed" || !url) return null;

      return (
        <Button
          unstyled
          onClick={() => window.open(url, "_blank")}
          className={styles.downloadButton}
          title="Download Report"
        >
          <Icon iconName="AttachFileIcon" />
        </Button>
      );
    },
  },
];

const ExportsPage = <Payload = void,>({
  exportType,
  createFn,
  pageTitle,
  description,
  cardTitle = "Recent Reports",
  buttonLabel = "Create New Report",
  successMessage,
  emptyText,
  extraColumns,
  renderAboveTable,
  renderCreateAction,
}: ExportsPageProps<Payload>) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.exports(id!, exportType);

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async (): Promise<ExportsQueryData> => {
      const response = await apiClient.sites.exportsDetail(
        Number(id),
        { s: "created_at DESC", "page[size]": 100, q: { type_eq: exportType } },
        { headers: { "Authorize-Collection-Actions": "create" } },
      );
      const responseData = response?.data as ExportsResponseWithAuth;
      const exports = (responseData?.exports ?? []).filter(
        (exp) => exp.type === exportType,
      );

      return { exports, authorizations: responseData?.authorizations };
    },
    enabled: !!id,
  });

  const canCreate = Boolean(
    data?.authorizations?.collection?.includes("create"),
  );
  const exportsList = useMemo(() => data?.exports ?? [], [data]);
  const columns = useMemo(
    () => [CREATED_AT_COLUMN, ...(extraColumns ?? []), ...TRAILING_COLUMNS],
    [extraColumns],
  );

  const { mutate: createExport, isPending: isCreating } = useMutation({
    mutationFn: (payload: Payload) => createFn(Number(id), payload),
    // Later status changes arrive over the site's channel (see SiteWrapper).
    onSuccess: (response) => {
      enqueueSnackbar(successMessage, { variant: "success" });
      upsertCachedExport(queryClient, id!, response.data.export);
    },
    onError: (err) => {
      enqueueSnackbar(messageFromError(err), {
        variant: "error",
        persist: true,
      });
    },
  });

  const createContext: CreateActionContext<Payload> = {
    create: createExport,
    isCreating,
    canCreate,
    exports: exportsList,
  };

  return (
    <BaseReportsPage title={pageTitle}>
      <div className={styles.container}>
        <p className={styles.description}>{description}</p>
        {renderAboveTable?.(createContext)}
        <Card title={cardTitle}>
          {isError ? (
            <div className={styles.errorState}>
              <Icon iconName="DangerIcon" className={styles.errorIcon} />
              <h4 className={styles.errorTitle}>Failed to Load Reports</h4>
              <p className={styles.errorText}>{error?.message}</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={exportsList}
              isLoading={isLoading}
              emptyText={emptyText}
              initialSorting={[{ id: "created_at", desc: true }]}
              pageSize={20}
              toolbarActions={
                renderCreateAction ? (
                  renderCreateAction(createContext)
                ) : (
                  <Button
                    onClick={() => createExport(undefined as Payload)}
                    disabled={isCreating || !canCreate}
                  >
                    {isCreating && <Spinner size="1rem" />}
                    {buttonLabel}
                  </Button>
                )
              }
            />
          )}
        </Card>
      </div>
    </BaseReportsPage>
  );
};

export default ExportsPage;
